import {
  getLeoUuid,
  LeoUUID,
  getList,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { EmbedDetail, EmbedDetailDTO } from "./embedDetail";
import { ProjectDetails, ProjectDetailsDTO } from "../types/projectDetails";
/* eslint-disable import/export */
export abstract class GetEmbedsRPC implements
  LeoRPC<GetEmbedsRPC.Request, GetEmbedsRPC.Response, GetEmbedsRPC.Errors.Errors>
{
  abstract execute(
    request: GetEmbedsRPC.Request
  ): Promise<LeoRPCResult<GetEmbedsRPC.Response, GetEmbedsRPC.Errors.Errors>>;
}

export namespace GetEmbedsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetEmbedsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID
    ) {
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetEmbedsRPC(projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    embedDetails: EmbedDetailDTO[];
    projectDetails: ProjectDetailsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetEmbedsResponse: boolean = true;
  
    readonly embedDetails: EmbedDetail[];
    readonly projectDetails: ProjectDetails;
  
    static readonly constraints = {
      "embedDetails": {
        "maxSize": 16,
      },
    }
  
    constructor(
    embedDetails: EmbedDetail[], 
    projectDetails: ProjectDetails
    ) {
      Response.validateEmbedDetails(embedDetails);
      this.embedDetails = embedDetails;
      this.projectDetails = projectDetails;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const embedDetails = getList<EmbedDetailDTO, EmbedDetail>(
        dto,
        "embedDetails",
        EmbedDetail.fromDTO,
        InvalidResponseEmbedDetailsError
      );
      const projectDetails = ProjectDetails.fromDTO(getObject(dto, "projectDetails", InvalidResponseProjectDetailsError));
      return new Response(
        embedDetails,
        projectDetails
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          embedDetails: this.embedDetails.map((e: EmbedDetail) => { return e.toDTO() }),
          projectDetails: this.projectDetails.toDTO()
      };
      return dto;
    }
    private static validateEmbedDetails = (embedDetails: EmbedDetail[]): void => {
      if(embedDetails.length > 16) {
        throw new InvalidResponseEmbedDetailsMaxSizeError(
          16,
          `Attribute embedDetails is too long. Size is ${embedDetails.length}. Max size is 16.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `GetEmbeds(embedDetails='${this.embedDetails}',projectDetails='${this.projectDetails}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetEmbedsRPC.INVALID_PROJECT_ID()`;
    };
    

    export type Errors = InvalidProjectId;
    export type ErrorsDTO = InvalidProjectIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetEmbedsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseEmbedDetailsError extends GetEmbedsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseEmbedDetailsError";
  }
}

export class InvalidResponseEmbedDetailsMaxSizeError extends InvalidResponseEmbedDetailsError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseEmbedDetailsMaxSizeError";
  }
}

export class InvalidResponseProjectDetailsError extends GetEmbedsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectDetailsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetEmbedsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

